import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useOutletContext } from 'react-router-dom';
import { useTranslation } from '@aimsos/i18n';
import { useDebounce } from '@aimsos/hooks';
import {
  Button,
  SearchInput,
  Badge,
  Pagination,
  StatusMessage,
  DataTable,
  Tooltip,
  useToast,
} from '@aimsos/ui';
import { AlertTriangle, Loader2, Search, UserCheck, UserX, Users, X } from 'lucide-react';

import { DeactivateUsersModal } from '../components';
import { useWorkspaceTenantUsers } from '../hooks';
import {
  formatDateShort,
  formatWorkspaceStatusLabel,
  isWorkspaceStatusActive,
} from '../utils';

const PAGE_SIZE_OPTIONS = [10, 20, 50];

/** Primary line for toast copy (email when present, else display name). */
const userToastLabel = (user) => {
  const email = user?.email?.trim();
  if (email) return email;
  const name = user?.fullName?.trim();
  if (name) return name;
  return user?.id ?? '';
};

const roleBadgeVariant = (user) => {
  if (!user.roleSummary) return 'neutral';
  const first = user.roleLabels[0]?.toLowerCase() ?? '';
  if (first.includes('admin')) return 'primary';
  return 'informative';
};

/**
 * Users tab — POST /v1/user/list (body: tenantUuid, page, sort, optional search).
 * Table layout with @aimsos/ui Checkbox / Badge / Button; bulk activate/deactivate via POST /v1/user/activate|deactivate.
 * Route: /workspaces/:workspaceId/users
 */
const WorkspaceUsersPage = () => {
  const { workspaceId } = useParams();
  const { t } = useTranslation('workspaces');
  const { loading: contextLoading } = useOutletContext();
  const toast = useToast();

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkUserModal, setBulkUserModal] = useState(null);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [messageScrollHover, setMessageScrollHover] = useState(false);
  const [tableScrollHover, setTableScrollHover] = useState(false);

  const debouncedSearchKeyword = useDebounce(search, 350);

  const { users, meta, listLoading, listError, refetchUsers, deactivateUsers, activateUsers } =
    useWorkspaceTenantUsers({
      workspaceId,
      contextLoading,
      page,
      setPage,
      pageSize,
      debouncedSearchKeyword,
    });

  useEffect(() => {
    setSelectedIds([]);
    setBulkUserModal(null);
  }, [debouncedSearchKeyword, page, pageSize, workspaceId]);

  const allPageSelected = useMemo(
    () => users.length > 0 && users.every((u) => selectedIds.includes(u.id)),
    [users, selectedIds],
  );

  const rangeLabel = useMemo(() => {
    const total = meta.total_items;
    if (total === 0 || users.length === 0) return '0-0';
    const start = (meta.current_page - 1) * meta.per_page + 1;
    const end = start + users.length - 1;
    return `${start}-${end}`;
  }, [meta.current_page, meta.per_page, meta.total_items, users.length]);

  const handleSearchChange = useCallback((e) => setSearch(e.target.value), []);
  const handleSearchClear = useCallback(() => setSearch(''), []);

  const setPageSizeAndReset = useCallback((next) => {
    setPageSize(next);
    setPage(1);
  }, []);

  const handlePrevPage = useCallback(() => {
    setPage((p) => Math.max(1, p - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  const toggleUser = useCallback((id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }, []);

  const toggleSelectAllPage = useCallback(() => {
    const pageIdSet = new Set(users.map((u) => u.id));
    if (allPageSelected) {
      setSelectedIds((prev) => prev.filter((id) => !pageIdSet.has(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...users.map((u) => u.id)])]);
    }
  }, [allPageSelected, users]);

  const clearSelection = useCallback(() => {
    setSelectedIds([]);
    setBulkUserModal(null);
  }, []);

  const bulkHomogeneousAction = useMemo(() => {
    const selected = users.filter((u) => selectedIds.includes(u.id));
    if (selected.length === 0) return null;
    const flags = selected.map((u) => isWorkspaceStatusActive(u.status));
    if (flags.every(Boolean)) return 'deactivate';
    if (flags.every((f) => !f)) return 'activate';
    return null;
  }, [users, selectedIds]);

  const openBulkUserModal = useCallback(() => {
    if (!bulkHomogeneousAction || selectedIds.length === 0) return;
    const targets = users.filter((u) => selectedIds.includes(u.id));
    if (targets.length === 0) return;
    setBulkUserModal({ variant: bulkHomogeneousAction, targets });
  }, [bulkHomogeneousAction, selectedIds, users]);

  const closeBulkUserModal = useCallback(() => {
    if (bulkSubmitting) return;
    setBulkUserModal(null);
  }, [bulkSubmitting]);

  const handleConfirmBulkUserAction = useCallback(async () => {
    if (!workspaceId || !bulkUserModal || bulkUserModal.targets.length === 0) return;

    const { variant, targets } = bulkUserModal;
    const count = targets.length;
    const fallbackKey =
      variant === 'deactivate' ? 'detail.users.deactivateFailed' : 'detail.users.activateFailed';

    setBulkSubmitting(true);
    try {
      const ids = targets.map((u) => u.id);
      if (variant === 'deactivate') {
        await deactivateUsers(workspaceId, ids);
      } else {
        await activateUsers(workspaceId, ids);
      }
      setSelectedIds([]);
      setBulkUserModal(null);
      await refetchUsers();

      if (variant === 'deactivate') {
        if (count === 1) {
          toast.success({
            title: t('detail.users.toasts.deactivatedTitleSingle', { label: userToastLabel(targets[0]) }),
            message: t('detail.users.toasts.deactivatedDescriptionSingle'),
            canClose: true,
          });
        } else {
          toast.success({
            title: t('detail.users.toasts.deactivatedTitleMany', { count }),
            message: t('detail.users.toasts.deactivatedDescriptionMany'),
            canClose: true,
          });
        }
      } else if (count === 1) {
        toast.success({
          title: t('detail.users.toasts.activatedTitleSingle', { label: userToastLabel(targets[0]) }),
          message: t('detail.users.toasts.activatedDescriptionSingle'),
          canClose: true,
        });
      } else {
        toast.success({
          title: t('detail.users.toasts.activatedTitleMany', { count }),
          message: t('detail.users.toasts.activatedDescriptionMany'),
          canClose: true,
        });
      }
    } catch (err) {
      toast.error({
        title: t('detail.users.toasts.errorTitle'),
        message: err?.message ?? t(fallbackKey),
        canClose: true,
      });
    } finally {
      setBulkSubmitting(false);
    }
  }, [workspaceId, bulkUserModal, deactivateUsers, activateUsers, refetchUsers, t, toast]);

  const joinedDate = useCallback((iso) => {
    if (!iso) return '—';
    const normalized = String(iso).includes('T') ? iso : String(iso).replace(' ', 'T');
    return formatDateShort(normalized);
  }, []);

  const columns = useMemo(
    () => [
      {
        key: 'fullName',
        label: t('detail.users.columns.name'),
        cellClassName: 'max-w-[200px]',
        render: (_, user) => (
          <span className="truncate text-sm font-medium text-content">{user.fullName}</span>
        ),
      },
      {
        key: 'roleSummary',
        label: t('detail.users.columns.role'),
        render: (_, user) => {
          if (!user.roleSummary) {
            return <span className="text-sm text-content-muted">—</span>;
          }
          const badge = (
            <Badge variant={roleBadgeVariant(user)} size="sm" className="max-w-[160px] truncate">
              {user.roleSummary}
            </Badge>
          );
          if (Array.isArray(user.roleLabels) && user.roleLabels.length > 1) {
            return (
              <Tooltip
                content={
                  <ul className="m-0 list-none space-y-1 p-0">
                    {user.roleLabels.map((label, i) => (
                      <li key={`${i}-${label}`}>{label}</li>
                    ))}
                  </ul>
                }
                position="top"
                maxWidth="280px"
              >
                {badge}
              </Tooltip>
            );
          }
          return badge;
        },
      },
      {
        key: 'email',
        label: t('detail.users.columns.email'),
        cellClassName: 'max-w-[240px]',
        render: (_, user) => (
          <span className="truncate text-sm text-content-secondary">{user.email}</span>
        ),
      },
      {
        key: 'status',
        label: t('detail.users.columns.status'),
        cellClassName: 'whitespace-nowrap',
        render: (_, user) =>
          user.status ? (
            <Badge
              variant={isWorkspaceStatusActive(user.status) ? 'success' : 'neutral'}
              size="small"
              className="!rounded-full"
            >
              {formatWorkspaceStatusLabel(user.status, t)}
            </Badge>
          ) : (
            <span className="text-sm text-content-muted">—</span>
          ),
      },
      {
        key: 'createdAt',
        label: t('detail.users.columns.dateAdded'),
        cellClassName: 'whitespace-nowrap text-content-muted',
        render: (_, user) => joinedDate(user.createdAt),
      },
    ],
    [t, joinedDate],
  );

  const tableSelection = useMemo(
    () => ({
      enabled: true,
      allSelected: allPageSelected,
      selectAllDisabled: listLoading,
      onToggleAll: toggleSelectAllPage,
      getRowId: (row) => row.id,
      isRowSelected: (row) => selectedIds.includes(row.id),
      onToggleRow: (row) => toggleUser(row.id),
      selectAllAriaLabel: t('detail.users.selectAllAria'),
      rowSelectAriaLabel: (row) => t('detail.users.selectUserAria', { name: row.fullName }),
    }),
    [allPageSelected, listLoading, selectedIds, toggleSelectAllPage, toggleUser, t],
  );

  if (contextLoading) {
    return (
      <div className="flex h-full min-h-0 flex-1 flex-col py-4">
        <StatusMessage
          icon={Loader2}
          iconClassName="animate-spin"
          title={t('common.loading')}
          subtitle={t('detail.tabLoadingSubtitle')}
        />
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col gap-4 py-4">
      <div className="flex shrink-0 flex-wrap items-center gap-4">
        <SearchInput
          value={search}
          onChange={handleSearchChange}
          onClear={handleSearchClear}
          placeholder={t('detail.users.searchPlaceholder')}
          className="max-w-sm"
        />
      </div>

      <div className="flex min-h-0 flex-1 flex-col gap-4">
        {listError && users.length === 0 && !listLoading ? (
          <div
            className="scrollbar-aimsos-auto flex min-h-0 flex-1 flex-col overflow-y-auto"
            data-scrollbar-hover={messageScrollHover ? 'true' : undefined}
            onMouseEnter={() => setMessageScrollHover(true)}
            onMouseLeave={() => setMessageScrollHover(false)}
          >
            <StatusMessage
              icon={AlertTriangle}
              title={t('detail.users.listError')}
              subtitle={listError}
            />
          </div>
        ) : !listLoading && users.length === 0 ? (
          <div
            className="scrollbar-aimsos-auto flex min-h-0 flex-1 flex-col overflow-y-auto"
            data-scrollbar-hover={messageScrollHover ? 'true' : undefined}
            onMouseEnter={() => setMessageScrollHover(true)}
            onMouseLeave={() => setMessageScrollHover(false)}
          >
            {debouncedSearchKeyword.trim() ? (
              <StatusMessage
                icon={Search}
                title={t('detail.users.noSearchResults')}
                subtitle={t('detail.users.noSearchResultsHint')}
              />
            ) : (
              <StatusMessage
                icon={Users}
                title={t('detail.users.empty.title')}
                subtitle={t('detail.users.empty.description')}
              />
            )}
          </div>
        ) : (
          <div className="flex min-h-0 flex-1 flex-col gap-4">
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-edge bg-surface">
              {selectedIds.length > 0 && (
                <div className="flex shrink-0 items-center justify-between gap-3 border-b border-[#2173FF]/35 bg-[#E9F1FF]/90 px-4 py-3 dark:border-blue-500/40 dark:bg-blue-950/40">
                  <span className="text-sm font-medium text-content">
                    {selectedIds.length === 1
                      ? t('detail.users.bulkSelectedOne')
                      : t('detail.users.bulkSelectedMany', { count: selectedIds.length })}
                  </span>
                  <div className="flex items-center gap-2">
                    {bulkHomogeneousAction && (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        leftIcon={bulkHomogeneousAction === 'deactivate' ? UserX : UserCheck}
                        loading={bulkSubmitting}
                        disabled={bulkSubmitting}
                        onClick={openBulkUserModal}
                      >
                        {bulkHomogeneousAction === 'deactivate'
                          ? t('detail.users.deactivate')
                          : t('detail.users.activate')}
                      </Button>
                    )}
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      leftIcon={X}
                      onClick={clearSelection}
                      aria-label={t('detail.users.clearSelectionAria')}
                    />
                  </div>
                </div>
              )}

              <div
                className="scrollbar-aimsos-auto min-h-0 flex-1 overflow-x-auto overflow-y-auto"
                data-scrollbar-hover={tableScrollHover ? 'true' : undefined}
                onMouseEnter={() => setTableScrollHover(true)}
                onMouseLeave={() => setTableScrollHover(false)}
              >
                <DataTable
                  columns={columns}
                  data={users}
                  loading={listLoading}
                  minTableWidth={720}
                  selection={tableSelection}
                />
              </div>
          </div>

          {meta.total_items > 0 && (
            <Pagination
              rangeLabel={rangeLabel}
              totalItems={meta.total_items}
              currentPage={meta.current_page}
              totalPages={meta.total_pages}
              hasPrevious={meta.has_previous}
              hasNext={meta.has_next}
              perPage={pageSize}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              onPrevPage={handlePrevPage}
              onNextPage={handleNextPage}
              onPageSizeChange={setPageSizeAndReset}
              disabled={listLoading}
            />
          )}
        </div>
      )}
      </div>

      <DeactivateUsersModal
        isOpen={Boolean(bulkUserModal)}
        onClose={closeBulkUserModal}
        variant={bulkUserModal?.variant ?? 'deactivate'}
        targets={bulkUserModal?.targets ?? []}
        submitting={bulkSubmitting}
        onConfirm={handleConfirmBulkUserAction}
      />
    </div>
  );
};

export default WorkspaceUsersPage;
